<template>
  <FormKit type="form" v-model="formData" @submit="onSubmit">
    <FormKitSchema :data="data" :schema="schema"> </FormKitSchema>
  </FormKit>
</template>

<script setup lang="ts">
import type { TemplateVariable } from '@/questionnaire/types'
import type { FormKitNode } from '@formkit/core'
import { watchEffect } from 'vue'
import { reactive, ref } from 'vue'

const props = defineProps<{
  templateVariable: TemplateVariable
  onSubmit?: (_data: TemplateVariable, _node?: FormKitNode) => Promise<unknown> | void
}>()

const formData = ref<TemplateVariable | undefined>(undefined)
const uniqueSibbling = (node: FormKitNode) => {
  const repeaterNode = node.parent?.parent
  if (repeaterNode && repeaterNode.props.type !== 'repeater') {
    console.warn('uniqueSibbling validation only works for children of a repeater FormKit node')
    return true
  }
  for (const sibblingGroup of repeaterNode?.children ?? []) {
    if (node.parent?.index == sibblingGroup.index) continue
    for (const sibbling of sibblingGroup.children ?? []) {
      if (sibbling.name === node.name && sibbling.value === node.value) {
        return false
      }
    }
  }
  return true
}

const uniqueSibblingMessage = ({ name, node }: { name: string; node: FormKitNode }) => {
  return `${name}: ${node.value} is not unique`
}

const data = reactive({
  defaultNodeType: (node: FormKitNode): string => {
    if (!node?.value) return 'text'
    switch (node.value) {
      case 'singleselect':
      case 'multiselect':
        return 'select'
      case 'text':
        return 'text'
      case 'date':
      case 'daterange':
        return 'date'
      case 'daysperweek':
      case 'number':
        return 'number'
      case 'currency':
        return 'currency'
      case 'boolean':
        return 'checkbox'
      case 'countryselect':
        return 'autocomplete'
      default:
        throw `missing node type mapping for ${node.value}`
    }
  },
})

const schema = reactive([
  {
    $el: 'div',
    attrs: {
      class: 'max-w-screen-md',
    },
    children: [
      { $formkit: 'text', name: 'label', label: 'Label', validation: 'required' },
      { $formkit: 'text', name: 'code', label: 'Code', validation: 'required' },
      {
        $formkit: 'select',
        name: 'input_type',
        label: 'Input Type',
        id: `input_type-${props.templateVariable.code}`,
        options: [
          'text',
          'boolean',
          'number',
          'singleselect',
          'multiselect',
          'countryselect',
          'date',
          'daterange',
          'daysperweek',
          'currency',
        ],
        validation: 'required',
      },
      {
        $formkit: 'number',
        if: `$get('input_type-${props.templateVariable.code}').value === 'number'`,
        name: 'min',
        label: 'Minimum',
      },
      {
        $formkit: 'number',
        if: `$get('input_type-${props.templateVariable.code}').value === 'number'`,
        name: 'max',
        label: 'Maximum',
      },
      {
        $cmp: 'FormKit',
        props: {
          type: `$defaultNodeType($get('input_type-${props.templateVariable.code}'))`,
          name: 'default',
          label: 'default',
        },
      },
      {
        $formkit: 'repeater',
        name: 'select_options',
        if: `$get('input_type-${props.templateVariable.code}').value === 'singleselect' || $get('input_type-${props.templateVariable.code}').value === 'multiselect'`,
        children: [
          {
            $formkit: 'text',
            name: 'label',
            label: 'Label',
            validation: 'uniqueSibbling|required',
            validationRules: { uniqueSibbling },
            validationMessages: { uniqueSibbling: uniqueSibblingMessage },
          },
          {
            $formkit: 'text',
            name: 'code',
            label: 'Code',
            validation: 'uniqueSibbling|required',

            validationRules: { uniqueSibbling },
            validationMessages: { uniqueSibbling: uniqueSibblingMessage },
          },
          {
            $formkit: 'hidden',
            name: 'id',
          },
        ],
      },
    ],
  },
])

watchEffect(() => {
  // TODO: fine a way to maybe just pass v-model to this component and use
  // the model in the form schema directly
  formData.value = { ...props.templateVariable }
})
</script>
